/* ========================================================================== *\
	IMPORTS
\* ========================================================================== */
import isDate from 'lodash/isDate';
import isFunction from 'lodash/isFunction';
import isNil from 'lodash/isNil';

import {
	isEarlier,
	isLater,
	removeTime
} from 'js-date-helper';

import { isNullableDateEqual, getNewNullableDateInstance } from './date.js';



/* ========================================================================== *\
	TYPE DEFINITIONS
\* ========================================================================== */

/**
 * @typedef {Object} DateRangeObject
 *
 * @property {Date} [start]
 * @property {Date} [end]
 */

/**
 * Callback method which will be called before a new range is applied. This
 * gives the callback the option to prevent the new range from getting applied.
 *
 * @callback WillChangeCallback
 *
 * @param {DateRangeObject} newRange The range that will become the
 *        active range.
 *
 * @returns {boolean} When the callback returns false the range will not be
 *          updated to the new values.
 */

/**
 * @typedef {Object} DateRangeOptions
 *
 * @property {boolean} [allowInfiniteRange=false]
 * @property {Function} hasChangedCallback
 * @property {WillChangeCallback} willChangeCallback
 */



/* ========================================================================== *\
	PRIVATE VARIABLES
\* ========================================================================== */
const
	propertyKey = {
		allowInfiniteRange: Symbol('allowOpenEnded'),
		hasChanged: Symbol('hasChanged'),
		end: Symbol('end'),
		start: Symbol('start'),
		willChange: Symbol('willChange')
	};



/* ========================================================================== *\
	PRIVATE METHODS
\* ========================================================================== */
/**
 * @param {Date} start
 * @param {Date} end
 *
 * @this DateRange
 */
function changeRange(start, end) {
	// When the range change is blocked exit the method before applying the
	// new range.
	if (!informRangeWillChange.call(this, {
		start,
		end
	})) {
		return;
	}

	// Update the property fields.
	this[propertyKey.end] = end;
	this[propertyKey.start] = start;

	// Inform the range has been changed.
	informRangeHasChanged.call(this);
}

/**
 * When callback is registered it will be called to inform the range has
 * been updated.
 *
 * @this DateRange
 */
function informRangeHasChanged() {
	if (!isFunction(this[propertyKey.hasChanged])) {
		return;
	}

	this[propertyKey.hasChanged]();
}

/**
 * When a callback is registered it will be called so it can decide to block the
 * new range before it is applied.
 *
 * @param {DateRangeObject} newRange The range about to be applied.
 *
 * @returns {boolean} Returns true when the new range can be applied.
 */
function informRangeWillChange(newRange) {
	if (!isFunction(this[propertyKey.willChange])) {
		return true;
	}

	try {
		// As long as the method doesn't return false the range is allowed to
		// be updated.
		return this[propertyKey.willChange](newRange) !== false;
	} catch (exception) {
		return true;
	}
}

/**
 *
 * @param {Date} date
 * @param {Date} boundaryDate
 * @param {Function} compare The method to use in order to check
 *
 * @returns {boolean} Returns true when either date is nil or when the date is
 *          determined to be within the boundary.
 */
function isDateInBoundary(date, boundaryDate, compare) {
	if (
		isNil(date) ||
		isNil(boundaryDate)
	) {
		return true;
	}

	return compare(date, boundaryDate);
}

/**
 *
 * @param {Date} start
 * @param {Date} end
 *
 * @returns {boolean}
 *
 * @this DateRange
 */
function isRangeValid(start, end) {
	// When infinite ranges are allowed the only requirement is that the start
	// date is before the end date.
	if (this[propertyKey.allowInfiniteRange]) {
		return isDateInBoundary(start, end, isEarlier);
	}

	// When infinite ranges are not allowed start and end need to be of the
	// same type, either both a Date instance or both null.
	if (
		isDate(start) !== isDate(end) ||
		(!isDate(start) && start !== null)
	) {
		return false;
	}

	return isDateInBoundary(start, end, isEarlier);
}

/**
 *
 * @param {Date} date
 *
 * @this DateRange
 */
function isValidNewEndDate(date) {
	if (isNullableDateEqual(date, this[propertyKey.end])) {
		return false;
	}

	// When no range start is set, the range end can't be set either.
	if (this.start === null && !this[propertyKey.allowInfiniteRange]) {
		return false;
	}

	// Check if the date is after the range start
	return isDateInBoundary(date, this.start, isLater);
}

/**
 *
 * @param {Date} date
 *
 * @this DateRange
 */
function isValidNewStartDate(date) {
	if (isNullableDateEqual(date, this[propertyKey.start])) {
		return false;
	}

	return isDateInBoundary(date, this.end, isEarlier);
}



/* ========================================================================== *\
	PUBLIC API
\* ========================================================================== */
export default class DateRange {
	/* ====================================================================== *\
		CONSTRUCTOR
	\* ====================================================================== */
	/**
	 *
	 * @param {DateRangeOptions} options
	 * @param {DateRangeObject} initialRange
	 */
	constructor(options, initialRange) {
		// Set the property which determines whether or not open ended ranges
		// are allowed for this instance of DateRange.
		this[propertyKey.allowInfiniteRange] = options ? !!options.allowInfiniteRange : false;

		// Set the initial range before setting the callback methods, this will
		// prevent the callbacks from getting called on the initial set.
		this[propertyKey.end] = null;
		this[propertyKey.start] = null;
		const
			start = (initialRange) ? initialRange.start : null,
			end = (initialRange) ? initialRange.end : null;

		this.updateRange(start, end);

		// Set the callback methods.
		this[propertyKey.hasChanged] = options ? options.hasChangedCallback : null;
		this[propertyKey.willChange] = options ? options.willChangeCallback : null;
	}



	/* ====================================================================== *\
		INSTANCE PROPERTIES
	\* ====================================================================== */
	/* ---------------------------------- *\
		range
	\* ---------------------------------- */
	/**
	 * @type {DateRangeObject}
	 *
	 * @readonly
	 * @memberof DateRange
	 */
	get range() {
		return {
			start: this.start,
			end: this.end
		};
	}

	/* ---------------------------------- *\
		rangeEnd
	\* ---------------------------------- */
	/**
	 * Property for the end of the selected range. It will be null when no
	 * range is selected, otherwise it will be an instance of Date.
	 *
	 * When setting the range it is important the new range end falls after any
	 * set range start date. Successfully setting the range will trigger a
	 * refresh of the calendar.
	 *
	 * When there is no range start set, the new value will not be set as the
	 * range end. Setting the range end to null will result in the range end to
	 * be set to the same date as the range start.
	 *
	 * @type {Date | null}
	 * @memberof Calendar
	 */
	get end() {
		return getNewNullableDateInstance(this[propertyKey.end]);
	}
	set end(value) {
		if (
			!isValidNewEndDate.call(this, value)
		) {
			return;
		}

		const
			newEnd = (isNil(value))
				? null
				: removeTime(value);

		if (this[propertyKey.allowInfiniteRange]) {
			changeRange.call(this, this[propertyKey.start], newEnd);

			return;
		}

		if (newEnd === null) {
			changeRange.call(this, this[propertyKey.start], this[propertyKey.start]);
		} else {
			changeRange.call(this, this[propertyKey.start], newEnd);
		}
	}

	/* ---------------------------------- *\
		rangeStart
	\* ---------------------------------- */
	/**
	 * Property for the start of the selected range. It will be null when no
	 * range is selected, otherwise it will be an instance of Date.
	 *
	 * When setting the range it is important the new range start falls within
	 * the configured min and max dates. The new start also has to be before any
	 * set range end date. Successfully setting the range will trigger a refresh
	 * of the calendar.
	 *
	 * There are two scenarios in which setting the range start will modify the
	 * value of the range end property.
	 * 1) When the new range start is null, the range end will also be set
	 *    to null
	 * 2) When the range start is set to a date and there is no range end set,
	 *    the range end will be set to the new range start.
	 *
	 * @see {@link updateRange} for information how to set the start and end of
	 *      the range without running into problems with a start date after the
	 *      current end date of the range.
	 *
	 * @type {Date | null}
	 * @memberof Calendar
	 */
	get start() {
		return getNewNullableDateInstance(this[propertyKey.start]);
	}
	set start(value) {
		// Check if the value is after the range end, when it is the new value
		// is not a valid new start date.
		if (
			!isValidNewStartDate.call(this, value)
		) {
			return;
		}

		const
			newStart = (isNil(value))
				? null
				: removeTime(value);

		if (this[propertyKey.allowInfiniteRange]) {
			changeRange.call(this, newStart, this[propertyKey.end]);

			return;
		}

		if (
			newStart === null ||
			this.end === null
		) {
			changeRange.call(this, newStart, newStart);
		} else {
			changeRange.call(this, newStart, this[propertyKey.end]);
		}
	}



	/* ====================================================================== *\
		PUBLIC METHODS
	\* ====================================================================== */
	/**
	 * Checks if the provided range is a subrange of the range.
	 *
	 * @param {DateRangeObject} range
	 *
	 * @returns {boolean} Returns true when the provided range is a subrange of
	 *          the range.
	 */
	containsSubrange(range) {
		if (
			isNil(range) ||
			!isRangeValid.call(this, range.start, range.end)
		) {
			return false;
		}

		return (
			this.isDateInRange(range.start) &&
			this.isDateInRange(range.end)
		);
	}

	/**
	 * Checks if the provided date falls within the range.
	 *
	 * @param {Date} date The date to check.
	 *
	 * @returns {boolean} Returns true when the provided date is within the
	 *          set range.
	 */
	isDateInRange(date) {
		return (
			isDateInBoundary(date, this.end, isEarlier) &&
			isDateInBoundary(date, this.start, isLater)
		);
	}

	/**
	 * Updates the start and end dates of the range.
	 *
	 * @param {Date} start The new start date
	 * @param {Date} end The new end date
	 */
	updateRange(start, end) {
		const
			newStart = (isDate(start))
				? removeTime(start)
				: null,
			newEnd = (isDate(end))
				? removeTime(end)
				: null;

		// When both parameters are not of type Date, it is not a valid range.
		// The new range is also invalid when the start is later than the end.
		if (
			(isNullableDateEqual(newStart, this.start) && isNullableDateEqual(newEnd, this.end)) ||
			!isRangeValid.call(this, start, end)
		) {
			return;
		}

		changeRange.call(this, newStart, newEnd);
	}
}
